import format from 'date-fns/format'
import isSameDay from 'date-fns/isSameDay'

import { MessageTypes } from '@/models/channels'

import { isQuestionnaireV2 } from './utils'

export const getNewMessageBar = ({ hasNewMessage, currentMessage }) => {
  if (
    !hasNewMessage &&
    !currentMessage?.isFromCurrentUser &&
    currentMessage?.isLastSyncedMessage
  ) {
    return {
      type: MessageTypes.SPLIT_BAR,
      content: 'Nova Mensagem',
      newMessages: true
    }
  }
}

export const getDateMessageBar = ({ oldMessage, currentMessage }) => {
  const oldMessageDate = oldMessage?.createdAt?.toDate()
  const currentMessageDate = currentMessage.createdAt?.toDate()

  if (
    currentMessageDate &&
    (!oldMessage || !isSameDay(oldMessageDate, currentMessageDate)) &&
    oldMessage?.type !== MessageTypes.SPLIT_BAR
  ) {
    return {
      type: MessageTypes.SPLIT_BAR,
      content: format(currentMessageDate, 'dd/MM/yy')
    }
  }
}

export const getVisualizationMessage = ({
  oldMessage,
  loggedStaffId,
  currentChannelParticipants,
  currentMessage
}) => {
  const visualization = currentChannelParticipants
    .filter(participant => {
      const participantLastSync = participant.lastSync?.seconds || 0

      return (
        oldMessage &&
        participant.id !== loggedStaffId &&
        participant.id !== oldMessage.userId &&
        participantLastSync > oldMessage?.createdAt?.seconds &&
        participantLastSync < currentMessage?.createdAt?.seconds
      )
    })
    .map(participant => participant.id)

  if (visualization.length) {
    return {
      type: MessageTypes.VISUALIZATION,
      content: visualization
    }
  }
}

export const getCurrentMessage = ({ currentMessage, channelPersonId }) => {
  return getMessageFormatted(currentMessage, channelPersonId)
}

/**
 * Transform the array of messages inserting the questionnaire controls with start, end and messages between.
 * @param {Object} messages -- Array of messages
 * @returns array of messages with questionnaire controls
 */
export const setQuestionnaireControls = messages => {
  let isQuestionnaire = false
  return messages.map(message => {
    if (message.type === MessageTypes.STARTED_QUESTIONNAIRE) {
      isQuestionnaire = true
    }

    if (isQuestionnaire) {
      message.isQuestionnaire = true
    }
    if (message.type === MessageTypes.ENDED_QUESTIONNAIRE) {
      isQuestionnaire = false
    }
    return message
  })
}

/**
 * Get formatted message
 * If is questionnaire types (QUESTION and ANSWER), get the message based on their versions
 * Version 1 returns the object like version 2
 * @param {Object} message
 * @param {String} userPersonalId
 * @returns array with formatted messages
 */
export const getMessageFormatted = (message, userPersonalId) => {
  const messages = []
  if (
    !isQuestionnaireV2(message) &&
    [MessageTypes.ANSWER, MessageTypes.QUESTION].includes(message.type)
  ) {
    if (message.type === MessageTypes.QUESTION) {
      const question = getQuestionV1(message)
      if (question) messages.push(question)
      const answer = getAnswerV1(message, userPersonalId)
      if (answer) messages.push(answer)
      return messages
    }
  } else {
    messages.push({ ...message })
  }
  return messages
}

/**
 * Questionnaire V1 transformers
 */

/**
 * If the message type is QUESTION, mount message with type ANSWER with selected
 * answers from question message object.
 * @param {Object} message -- The message object
 * @param {String} channelPersonId -- User id
 * @returns null if has not message or message type is not question and
 * return the answer object if message type is QUESTION
 */
export const getAnswerV1 = (message, channelPersonId) => {
  if (!message) {
    return null
  }
  if (message.type === MessageTypes.QUESTION) {
    const selectedAnswer = getAnswersFromQuestionV1(message)

    const createdAt =
      message?.additionalInfo?.status?.member_answered_at || message.createdAt

    if (selectedAnswer && selectedAnswer.length) {
      const prefix = selectedAnswer.length > 1 ? '&bull; ' : ''
      return {
        createdAt,
        type: MessageTypes.ANSWER,
        content: selectedAnswer.map(answer => prefix + answer).join('\\n'),
        userId: channelPersonId,
        id: MessageTypes.ANSWER + '_' + message.id
      }
    }
  }
  return null
}

/**
 * Get selected answers from message type QUESTION
 * @param {Object} message -- Message object
 * @returns Array with the selected answers label
 */
export const getAnswersFromQuestionV1 = message => {
  if (message.type !== MessageTypes.QUESTION) {
    return null
  }
  const selectedAnswer = message?.additionalInfo?.input?.options
    ?.filter(answer => answer.selected)
    ?.map(answer => answer.label)
  return selectedAnswer
}

export const getQuestionV1 = message => {
  if (!message) {
    return null
  }
  if (message.type === MessageTypes.QUESTION) {
    return {
      ...message,
      question: {
        input: {
          options: message?.additionalInfo?.input?.options?.map(option => {
            return {
              label: option.label
            }
          })
        }
      }
    }
  }
  return null
}
